import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { Check, Home, Droplets, Wind, ArrowRight } from "lucide-react";
import { getServiceSchema } from "../utils/structuredData";

export const GlassVerandas = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Glass Verandas & Roof Systems | Giallo Plus | Hudson Knox"
        description="Elegant glass verandas with aluminium frames. Deponti Giallo Plus systems for year-round outdoor living. Professional installation in London, Kent & South East England."
        keywords="glass verandas, glass roof, Giallo Plus, outdoor canopy, glass pergola, aluminium veranda, garden veranda, patio cover"
        canonicalPath="/services/glass-verandas"
        structuredData={getServiceSchema(
          "Glass Verandas",
          "Installation of Deponti Giallo Plus glass veranda systems",
          "https://hudsonknox.co.uk/services/glass-verandas"
        )}
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/6775268/pexels-photo-6775268.jpeg)",
            filter: "brightness(0.4)"
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Glass Verandas</h1>
          <p className="text-xl text-neutral-200">Elegant glass structures that extend your living space</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-light text-neutral-900 mb-6">Modern Architectural Excellence</h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              A glass veranda combines sleek aluminium framework with tempered glass panels to create a stunning 
              architectural feature that protects your outdoor space from the elements while maintaining an open, 
              light-filled atmosphere. Perfect for extending your living space and enjoying your garden year-round.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-light text-neutral-900 mb-6">Why Choose a Glass Veranda?</h3>
              <p className="text-lg text-neutral-600 mb-6">
                Glass verandas offer the ultimate in outdoor living sophistication. They provide complete protection 
                from rain and weather while creating a bright, airy space that feels connected to your garden.
              </p>
              <div className="space-y-4">
                {[
                  "Complete rain protection",
                  "Maximum natural light",
                  "Contemporary architectural style",
                  "Low maintenance glass and aluminium",
                  "Increases property value significantly",
                  "Optional integrated lighting and heating",
                  "Self-cleaning glass options",
                  "Year-round outdoor use"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-6 w-6 text-neutral-900 flex-shrink-0 mt-1" />
                    <span className="text-neutral-600 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1523477593243-78bbf626fd3b"
                alt="Glass veranda structure"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light mb-12 text-center">Technical Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Home className="h-8 w-8" />,
                title: "Aluminium Frame",
                description: "Powder-coated aluminium in various colours to match your property"
              },
              {
                icon: <Droplets className="h-8 w-8" />,
                title: "Integrated Drainage",
                description: "Hidden gutter system channels rainwater away efficiently"
              },
              {
                icon: <Wind className="h-8 w-8" />,
                title: "Wind Rated",
                description: "Engineered to withstand high winds and heavy snow loads"
              },
              {
                icon: <Check className="h-8 w-8" />,
                title: "Tempered Glass",
                description: "Safety glass panels for maximum protection and durability"
              }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-lg mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium mb-3">{feature.title}</h3>
                <p className="text-neutral-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Options */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Design Customisation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Glass Options",
                items: ["Clear tempered glass", "Self-cleaning glass", "Tinted glass", "Frosted glass panels"]
              },
              {
                title: "Frame Colours",
                items: ["Anthracite grey", "Pure white", "Black", "Custom RAL colours"]
              },
              {
                title: "Extras & Upgrades",
                items: ["LED lighting strips", "Infrared heating", "Side glazing", "Automated blinds"]
              }
            ].map((option, index) => (
              <Card key={index} className="border-neutral-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-medium text-neutral-900 mb-4">{option.title}</h3>
                  <ul className="space-y-2">
                    {option.items.map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-neutral-600">
                        <div className="w-1.5 h-1.5 bg-neutral-900 rounded-full" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Perfect For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Patio Coverage",
                description: "Protect your patio furniture and create a weatherproof outdoor dining area",
                image: "https://images.unsplash.com/photo-1610036615775-f5814e8bd4df"
              },
              {
                title: "Garden Rooms",
                description: "Create an additional living space that connects seamlessly with your garden",
                image: "https://images.unsplash.com/photo-1560990883-9b76fec399a9"
              },
              {
                title: "Hot Tub Shelter",
                description: "Enjoy your hot tub year-round with overhead protection from the elements",
                image: "https://images.unsplash.com/photo-1766603636766-1f4662469448"
              },
              {
                title: "Pool Side Coverage",
                description: "Extend your pool season with a covered relaxation area",
                image: "https://images.unsplash.com/photo-1760067538559-49b2bfb5f42a"
              }
            ].map((application, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative h-64 rounded-lg overflow-hidden mb-4">
                  <img
                    src={application.image}
                    alt={application.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-2xl font-medium mb-2">{application.title}</h3>
                    <p className="text-neutral-200 text-sm">{application.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-white mb-4">Extend Your Living Space</h2>
            <p className="text-lg text-neutral-400">Request a quote for your glass veranda installation</p>
          </div>
          <QuoteForm title="Request Glass Veranda Quote" />
        </div>
      </section>
    </div>
  );
};
